import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ChevronDown, ChevronRight, Save, Upload, FileText, ShieldCheck, AlertTriangle,
  ArrowLeft, FileDown, Trash2, CheckCircle, XCircle, Sparkles, Loader2,
} from "lucide-react";
import {
  TIER_LABELS, TIER_COLORS, FUNCTION_COLORS, scoreToColor, IMPL_STATUS_LABELS, EVIDENCE_TYPE_LABELS,
} from "@/lib/constants";

const SCORE_LABELS = {
  0: "Not implemented",
  1: "Ad hoc / reactive",
  2: "Defined but inconsistent",
  3: "Implemented and repeatable",
  4: "Managed and measured",
  5: "Optimized / continuously improved",
};

export default function AssessmentWorkspace() {
  const { id } = useParams();
  const { user } = useAuth();
  const canEdit = ["super_admin", "assessor"].includes(user?.role);
  const [assessment, setAssessment] = useState(null);
  const [tree, setTree] = useState([]);
  const [responses, setResponses] = useState({}); // by subcategory_id
  const [evidence, setEvidence] = useState({}); // by subcategory_id
  const [scores, setScores] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("workspace");
  const [activeFunction, setActiveFunction] = useState("GV");
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [a, t, r, e, s] = await Promise.all([
        api.get(`/assessments/${id}`),
        api.get("/framework/tree"),
        api.get(`/assessments/${id}/responses`),
        api.get(`/evidence?assessment_id=${id}`),
        api.get(`/assessments/${id}/scores`),
      ]);
      setAssessment(a.data);
      setTree(t.data);
      const rmap = {};
      r.data.forEach((x) => { rmap[x.subcategory_id] = x; });
      setResponses(rmap);
      const emap = {};
      e.data.forEach((x) => {
        if (!emap[x.subcategory_id]) emap[x.subcategory_id] = [];
        emap[x.subcategory_id].push(x);
      });
      setEvidence(emap);
      setScores(s.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [id]);

  if (loading || !assessment) {
    return <div className="text-zinc-500 text-sm" data-testid="workspace-loading">Loading workspace…</div>;
  }

  const activeFn = tree.find((f) => f.code === activeFunction) || tree[0];

  return (
    <div data-testid="assessment-workspace">
      <Link to="/assessments" className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-white">
        <ArrowLeft className="w-3 h-3" /> Back to Assessments
      </Link>
      <div className="mt-3 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-uppercase">{assessment.client?.name} · {assessment.client?.industry}</div>
          <h1 className="text-3xl tracking-tight font-medium mt-2 text-white" data-testid="assessment-title">{assessment.name}</h1>
          <div className="text-xs text-zinc-500 mt-1 mono">
            {assessment.assessment_date} · Assessor: {assessment.assessor_name} · Status: {assessment.status.replace("_", " ")}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" className="border-zinc-800 text-zinc-300 hover:bg-zinc-900" data-testid="download-pdf-button">
            <a href={`${BACKEND_URL}/api/reports/assessment/${id}/pdf?auth=${localStorage.getItem("access_token")}`} target="_blank" rel="noreferrer">
              <FileDown className="w-3.5 h-3.5 mr-1.5" /> Report PDF
            </a>
          </Button>
          <Button asChild variant="outline" className="border-zinc-800 text-zinc-300 hover:bg-zinc-900" data-testid="download-csv-button">
            <a href={`${BACKEND_URL}/api/reports/assessment/${id}/csv?auth=${localStorage.getItem("access_token")}`} target="_blank" rel="noreferrer">
              <FileDown className="w-3.5 h-3.5 mr-1.5" /> CSV
            </a>
          </Button>
        </div>
      </div>

      {/* Score header */}
      {scores && (
        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="workspace-score-header">
          <div className="rounded-md border border-zinc-800 bg-[#121212] p-4">
            <div className="label-uppercase">Overall</div>
            <div className="mt-2 flex items-baseline gap-1">
              <span className="text-3xl mono font-medium" style={{ color: scoreToColor(scores.overall_score) }}>{scores.overall_score.toFixed(2)}</span>
              <span className="text-zinc-600 mono text-sm">/ 5</span>
            </div>
            <div className="mt-2"><Badge className={`${TIER_COLORS[scores.overall_tier].bg} ${TIER_COLORS[scores.overall_tier].text} ${TIER_COLORS[scores.overall_tier].border} text-[10px]`}>{TIER_LABELS[scores.overall_tier]}</Badge></div>
          </div>
          <div className="rounded-md border border-zinc-800 bg-[#121212] p-4">
            <div className="label-uppercase">Coverage</div>
            <div className="mt-2 text-3xl mono font-medium text-white">{scores.responded_subcategories}<span className="text-zinc-600 text-base">/{scores.total_subcategories}</span></div>
            <div className="text-[10px] text-zinc-500 mt-1">subcategories scored</div>
          </div>
          <div className="rounded-md border border-zinc-800 bg-[#121212] p-4">
            <div className="label-uppercase">Evidence</div>
            <div className="mt-2 text-3xl mono font-medium text-white">{scores.evidence_completion_pct}%</div>
            <div className="text-[10px] text-zinc-500 mt-1">subcategories with evidence</div>
          </div>
          <div className="rounded-md border border-zinc-800 bg-[#121212] p-4">
            <div className="label-uppercase">Critical Gaps</div>
            <div className="mt-2 text-3xl mono font-medium" style={{ color: scores.critical_gaps.length ? "#EF4444" : "#FAFAFA" }}>{scores.critical_gaps.length}</div>
            {scores.has_critical_violation && <div className="text-[10px] text-red-400 mt-1 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Tier capped @ 2</div>}
          </div>
        </div>
      )}

      {/* Function tabs */}
      <div className="mt-5 flex gap-1 border-b border-zinc-800 overflow-x-auto" data-testid="function-tabs">
        {tree.map((f) => {
          const fScore = scores?.functions.find((x) => x.function_code === f.code);
          const active = activeFunction === f.code;
          return (
            <button
              key={f.code}
              onClick={() => setActiveFunction(f.code)}
              data-testid={`function-tab-${f.code}`}
              className={`px-4 py-3 text-sm flex items-center gap-2 border-b-2 transition-colors ${
                active ? "border-white text-white" : "border-transparent text-zinc-400 hover:text-white"
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: FUNCTION_COLORS[f.code] }} />
              <span className="font-medium">{f.code}</span>
              <span className="hidden md:inline text-zinc-500">{f.name}</span>
              {fScore && <span className="ml-1 text-xs mono" style={{ color: scoreToColor(fScore.score) }}>{fScore.score.toFixed(1)}</span>}
            </button>
          );
        })}
      </div>

      {/* Categories + subcategories */}
      <div className="mt-4 space-y-3">
        {activeFn?.categories.map((c) => {
          const isOpen = expanded[c.code] ?? true;
          return (
            <div key={c.code} className="rounded-md border border-zinc-800 bg-[#121212] overflow-hidden" data-testid={`category-${c.code}`}>
              <button
                onClick={() => setExpanded((s) => ({ ...s, [c.code]: !isOpen }))}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-zinc-900/40"
              >
                <div className="flex items-center gap-2">
                  {isOpen ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
                  <span className="text-xs mono text-white font-medium">{c.code}</span>
                  <span className="text-sm text-zinc-300">{c.name}</span>
                </div>
                <span className="text-[10px] text-zinc-500 mono">{c.subcategories.length} subcategories</span>
              </button>
              {isOpen && (
                <div className="border-t border-zinc-800 divide-y divide-zinc-800">
                  {c.subcategories.map((sub) => (
                    <SubcategoryRow
                      key={sub.id}
                      subcategory={sub}
                      response={responses[sub.id]}
                      evidence={evidence[sub.id] || []}
                      assessmentId={id}
                      canEdit={canEdit}
                      onChanged={load}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SubcategoryRow({ subcategory, response, evidence, assessmentId, canEdit, onChanged }) {
  const [open, setOpen] = useState(false);
  const adjusted = response?.adjusted_score ?? 0;
  const target = response?.target_score ?? 4;
  const evCount = evidence.filter((e) => !e.is_deleted).length;

  return (
    <div className="px-4 py-3" data-testid={`sub-${subcategory.subcategory_code}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-start justify-between gap-3 text-left">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs mono text-white font-medium">{subcategory.subcategory_code}</span>
            {subcategory.critical && <Badge className="bg-red-500/10 text-red-400 border-red-500/30 text-[10px] py-0">CRITICAL</Badge>}
            <Badge className="bg-zinc-900 text-zinc-400 border-zinc-800 text-[10px] py-0 mono">w {subcategory.default_weight.toFixed(1)}</Badge>
            {evCount > 0 && <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/30 text-[10px] py-0">{evCount} evidence</Badge>}
          </div>
          <div className="text-[12px] text-zinc-400 mt-1 leading-relaxed">{subcategory.description}</div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="text-right">
            <div className="text-[10px] text-zinc-500 label-uppercase">Score</div>
            <div className="text-lg mono font-medium" style={{ color: scoreToColor(adjusted) }}>{adjusted.toFixed(1)}</div>
            <div className="text-[10px] text-zinc-600 mono">→ {target.toFixed(1)}</div>
          </div>
          {open ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
        </div>
      </button>
      {open && (
        <div className="mt-3 border-t border-zinc-800 pt-3">
          <ScoringPanel
            subcategory={subcategory} response={response} evidence={evidence}
            assessmentId={assessmentId} canEdit={canEdit} onChanged={onChanged}
          />
        </div>
      )}
    </div>
  );
}

function ScoringPanel({ subcategory, response, evidence, assessmentId, canEdit, onChanged }) {
  const [form, setForm] = useState({
    raw_score: response?.raw_score ?? 0,
    target_score: response?.target_score ?? 4,
    implementation_status: response?.implementation_status ?? "not_implemented",
    notes: response?.notes ?? "",
    assessor_comments: response?.assessor_comments ?? "",
    remediation_recommendation: response?.remediation_recommendation ?? "",
  });
  const [busy, setBusy] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/assessments/${assessmentId}/responses`, {
        assessment_id: assessmentId,
        subcategory_id: subcategory.id,
        ...form,
        raw_score: Number(form.raw_score),
        target_score: Number(form.target_score),
      });
      toast.success("Saved");
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const ev = evidence.filter((e) => !e.is_deleted);
  const evidenceStrength = ev.some((e) => e.strength === "verified") ? "verified" : (ev.length > 0 ? "weak" : "none");
  const cap = { none: 2, weak: 3, verified: 5 }[evidenceStrength];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid={`scoring-panel-${subcategory.subcategory_code}`}>
      <div className="lg:col-span-2 space-y-4">
        <div>
          <Label className="label-uppercase">Maturity Score (0–5)</Label>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {[0, 1, 2, 3, 4, 5].map((n) => {
              const disabled = !canEdit || n > cap;
              return (
                <button
                  key={n}
                  disabled={disabled}
                  onClick={() => setForm({ ...form, raw_score: n })}
                  data-testid={`score-button-${subcategory.subcategory_code}-${n}`}
                  className={`px-3 py-2 rounded-md border text-sm transition-colors ${
                    Number(form.raw_score) === n
                      ? "bg-white text-black border-white"
                      : disabled
                        ? "border-zinc-800 text-zinc-700 cursor-not-allowed"
                        : "border-zinc-800 text-zinc-300 hover:border-zinc-600 hover:text-white"
                  }`}
                  title={SCORE_LABELS[n]}
                >
                  <span className="mono font-medium">{n}</span>
                  <span className="ml-1.5 text-[10px] hidden sm:inline">{SCORE_LABELS[n]}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-2 text-[11px] text-zinc-500">
            Evidence strength: <span className="text-white mono">{evidenceStrength}</span> · Cap: <span className="text-white mono">{cap}</span>
            {Number(form.raw_score) > cap && <span className="text-red-400 ml-2">(score capped to {cap})</span>}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-uppercase">Implementation Status</Label>
            <select
              value={form.implementation_status}
              onChange={(e) => setForm({ ...form, implementation_status: e.target.value })}
              disabled={!canEdit}
              data-testid={`impl-status-${subcategory.subcategory_code}`}
              className="mt-1.5 w-full h-10 px-3 rounded-md bg-[#0A0A0A] border border-zinc-800 text-sm text-white"
            >
              {Object.entries(IMPL_STATUS_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div>
            <Label className="label-uppercase">Target Score</Label>
            <select
              value={form.target_score}
              onChange={(e) => setForm({ ...form, target_score: Number(e.target.value) })}
              disabled={!canEdit}
              data-testid={`target-score-${subcategory.subcategory_code}`}
              className="mt-1.5 w-full h-10 px-3 rounded-md bg-[#0A0A0A] border border-zinc-800 text-sm text-white"
            >
              {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n} — {SCORE_LABELS[n]}</option>)}
            </select>
          </div>
        </div>

        <Field label="Notes">
          <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} disabled={!canEdit}
            data-testid={`notes-${subcategory.subcategory_code}`} className="bg-[#0A0A0A] border-zinc-800" />
        </Field>
        <Field label="Assessor Comments">
          <Textarea rows={2} value={form.assessor_comments} onChange={(e) => setForm({ ...form, assessor_comments: e.target.value })} disabled={!canEdit}
            data-testid={`comments-${subcategory.subcategory_code}`} className="bg-[#0A0A0A] border-zinc-800" />
        </Field>
        <Field label="Remediation Recommendation">
          <Textarea rows={2} value={form.remediation_recommendation} onChange={(e) => setForm({ ...form, remediation_recommendation: e.target.value })} disabled={!canEdit}
            data-testid={`remediation-${subcategory.subcategory_code}`} className="bg-[#0A0A0A] border-zinc-800" />
        </Field>

        {canEdit && (
          <Button onClick={save} disabled={busy} data-testid={`save-${subcategory.subcategory_code}`} className="bg-white text-black hover:bg-zinc-200">
            <Save className="w-3.5 h-3.5 mr-1.5" /> {busy ? "Saving…" : "Save"}
          </Button>
        )}
      </div>

      {/* Evidence panel */}
      <div className="rounded-md border border-zinc-800 bg-[#0A0A0A] p-4">
        <div className="flex items-center justify-between">
          <div className="label-uppercase">Evidence</div>
          {canEdit && (
            <Button size="sm" variant="outline" onClick={() => setUploadOpen(true)} data-testid={`upload-evidence-${subcategory.subcategory_code}`} className="h-7 px-2 text-[11px] border-zinc-800 text-zinc-300 hover:bg-zinc-900">
              <Upload className="w-3 h-3 mr-1" /> Upload
            </Button>
          )}
        </div>
        <div className="mt-3 space-y-2">
          {ev.length === 0 && <div className="text-xs text-zinc-500">No evidence uploaded.</div>}
          {ev.map((e) => (
            <EvidenceItem key={e.id} item={e} canEdit={canEdit} onChanged={onChanged} />
          ))}
        </div>
        <div className="mt-3 text-[10px] text-zinc-500 leading-relaxed border-t border-zinc-800 pt-2">
          <b className="text-zinc-300">Cap policy:</b> No evidence = max 2 · Weak = max 3 · Verified = max 5.
        </div>
      </div>

      <UploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        assessmentId={assessmentId}
        subcategoryId={subcategory.id}
        onUploaded={onChanged}
      />
    </div>
  );
}

function Field({ label, children }) {
  return (<div><Label className="label-uppercase">{label}</Label><div className="mt-1.5">{children}</div></div>);
}

function EvidenceItem({ item, canEdit, onChanged }) {
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const remove = async () => {
    if (!confirm("Delete evidence?")) return;
    try { await api.delete(`/evidence/${item.id}`); toast.success("Removed"); onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const analyze = async () => {
    setAnalyzing(true);
    try {
      await api.post(`/evidence/${item.id}/analyze`);
      toast.success("AI analysis complete");
      setShowAnalysis(true);
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setAnalyzing(false); }
  };
  const strengthColor = item.strength === "verified" ? "text-green-400" : item.strength === "weak" ? "text-yellow-400" : "text-red-400";
  const ai = item.ai_analysis;

  return (
    <div className="rounded-md border border-zinc-800 p-2.5 bg-[#121212]" data-testid={`evidence-item-${item.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <FileText className="w-3 h-3 text-zinc-500" />
            <span className="text-xs text-white truncate">{item.original_filename}</span>
          </div>
          <div className="flex items-center gap-2 mt-1 text-[10px] text-zinc-500">
            <span>{EVIDENCE_TYPE_LABELS[item.evidence_type] || item.evidence_type}</span>
            <span>·</span>
            <span className={strengthColor}>{item.strength}</span>
            {item.verified && <CheckCircle className="w-3 h-3 text-green-400" />}
            {ai && <AiVerdictBadge ai={ai} />}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {canEdit && (
            <>
              <button onClick={analyze} disabled={analyzing} className="p-1 rounded hover:bg-blue-500/10 text-zinc-400 hover:text-blue-400 disabled:opacity-50" data-testid={`analyze-${item.id}`} title="Analyze with AI (OCR + compliance grade)">
                {analyzing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              </button>
              <button onClick={() => setVerifyOpen(true)} className="p-1 rounded hover:bg-zinc-800 text-zinc-400 hover:text-white" data-testid={`verify-${item.id}`} title="Verify">
                <ShieldCheck className="w-3 h-3" />
              </button>
              <button onClick={remove} className="p-1 rounded hover:bg-red-500/10 text-zinc-400 hover:text-red-400" data-testid={`del-evidence-${item.id}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </>
          )}
        </div>
      </div>
      {ai && (
        <div className="mt-2">
          <button onClick={() => setShowAnalysis((v) => !v)} className="flex items-center gap-1 text-[10px] text-blue-400 hover:text-blue-300" data-testid={`toggle-analysis-${item.id}`}>
            <Sparkles className="w-3 h-3" /> AI analysis
            {showAnalysis ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
          {showAnalysis && <AiAnalysisPanel ai={ai} />}
        </div>
      )}
      <VerifyDialog open={verifyOpen} onClose={() => setVerifyOpen(false)} item={item} onChanged={onChanged} />
    </div>
  );
}

const AI_VERDICT_META = {
  compliant: { label: "Compliant", cls: "bg-green-500/10 text-green-400 border-green-500/30" },
  partially_compliant: { label: "Partial", cls: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30" },
  non_compliant: { label: "Non-compliant", cls: "bg-red-500/10 text-red-400 border-red-500/30" },
  not_evidence: { label: "Not evidence", cls: "bg-zinc-700/40 text-zinc-400 border-zinc-700" },
};

const MET_META = {
  yes: { icon: "✓", cls: "text-green-400" },
  partial: { icon: "~", cls: "text-yellow-400" },
  no: { icon: "✗", cls: "text-red-400" },
};

function suffMeta(ai) {
  return ai?.sufficient
    ? { label: "PASS · Sufficient", cls: "bg-green-500/15 text-green-300 border-green-500/40" }
    : { label: "FAIL · Insufficient", cls: "bg-red-500/15 text-red-300 border-red-500/40" };
}

function AiVerdictBadge({ ai }) {
  const s = suffMeta(ai);
  return (
    <Badge className={`${s.cls} text-[9px] py-0 h-4`} title="AI sufficiency decision (advisory)">
      <Sparkles className="w-2.5 h-2.5 mr-0.5 inline" />{ai.sufficient ? "PASS" : "FAIL"}{typeof ai.score === "number" ? ` · ${ai.score}` : ""}
    </Badge>
  );
}

function RequirementsChecklist({ checks }) {
  if (!checks?.length) return null;
  return (
    <div>
      <div className="text-zinc-500 mb-0.5">Requirement checks</div>
      <ul className="space-y-1">
        {checks.map((c, i) => {
          const mm = MET_META[c.met] || MET_META.no;
          return (
            <li key={i} className="flex gap-1.5">
              <span className={`${mm.cls} mono shrink-0`} title={c.met}>{mm.icon}</span>
              <span><span className="text-zinc-200">{c.requirement}</span>{c.note ? <span className="text-zinc-500"> — {c.note}</span> : null}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function AiAnalysisPanel({ ai }) {
  const m = AI_VERDICT_META[ai.verdict] || AI_VERDICT_META.not_evidence;
  const s = suffMeta(ai);
  const passReasons = ai.reasons_pass || ai.key_findings || [];
  const failReasons = ai.reasons_fail || ai.gaps || [];
  return (
    <div className="mt-2 rounded-md border border-zinc-800 bg-[#0A0A0A] p-2.5 space-y-2 text-[11px]">
      <div className="flex items-center gap-2 flex-wrap">
        <Badge className={s.cls}>{s.label}</Badge>
        <Badge className={m.cls}>{m.label}</Badge>
        <span className="text-zinc-400">Score <span className="text-white mono">{ai.score}/100</span></span>
        <span className="text-zinc-500">Suggested strength: <span className={ai.suggested_strength === "verified" ? "text-green-400" : ai.suggested_strength === "weak" ? "text-yellow-400" : "text-red-400"}>{ai.suggested_strength}</span></span>
      </div>
      {ai.document_type && <div className="text-zinc-500">Document: <span className="text-zinc-300">{ai.document_type}</span></div>}
      {ai.summary && <div className="text-zinc-300">{ai.summary}</div>}
      {ai.rationale && (
        <div className={ai.sufficient ? "text-green-300/90" : "text-red-300/90"}>
          <span className="text-zinc-500">Why it {ai.sufficient ? "passed" : "failed"}: </span>{ai.rationale}
        </div>
      )}
      <RequirementsChecklist checks={ai.requirements_check} />
      {passReasons.length > 0 && (
        <div>
          <div className="text-zinc-500 mb-0.5">Why it passes</div>
          <ul className="list-disc list-inside text-green-300/80 space-y-0.5">
            {passReasons.map((f, i) => <li key={i}>{f}</li>)}
          </ul>
        </div>
      )}
      {failReasons.length > 0 && (
        <div>
          <div className="text-zinc-500 mb-0.5">Why it falls short</div>
          <ul className="list-disc list-inside text-yellow-300/80 space-y-0.5">
            {failReasons.map((g, i) => <li key={i}>{g}</li>)}
          </ul>
        </div>
      )}
      {ai.extracted_text && (
        <details className="text-zinc-500">
          <summary className="cursor-pointer hover:text-zinc-300">Extracted text (OCR)</summary>
          <pre className="mt-1 whitespace-pre-wrap text-[10px] text-zinc-400 max-h-48 overflow-auto">{ai.extracted_text}</pre>
        </details>
      )}
      <div className="text-[9px] text-zinc-600 pt-1 border-t border-zinc-800">Advisory only — AI does not change the evidence strength or score. {ai.model ? `Model: ${ai.model}` : ""}</div>
    </div>
  );
}

function VerifyDialog({ open, onClose, item, onChanged }) {
  const [form, setForm] = useState({ verified: false, strength: "weak", verifier_notes: "" });
  useEffect(() => {
    if (open) setForm({ verified: !!item.verified, strength: item.strength || "weak", verifier_notes: item.verifier_notes || "" });
  }, [open, item]);
  const submit = async () => {
    try { await api.patch(`/evidence/${item.id}/verify`, form); toast.success("Verification updated"); onChanged(); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121212] border-zinc-800 text-white sm:max-w-md" data-testid={`verify-dialog-${item.id}`}>
        <DialogHeader><DialogTitle>Verify Evidence</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-zinc-500 mono">{item.original_filename}</div>
          <div>
            <Label className="label-uppercase">Strength</Label>
            <div className="mt-1.5 grid grid-cols-3 gap-2">
              {["none", "weak", "verified"].map((s) => (
                <button key={s} onClick={() => setForm({ ...form, strength: s, verified: s === "verified" })}
                  className={`px-3 py-2 rounded-md border text-xs ${form.strength === s ? "bg-white text-black border-white" : "border-zinc-800 text-zinc-300 hover:border-zinc-600"}`}
                  data-testid={`strength-${s}`}>
                  {s.toUpperCase()}
                </button>
              ))}
            </div>
            <div className="text-[10px] text-zinc-500 mt-2">Verified raises score cap to 5; weak caps at 3; none caps at 2.</div>
          </div>
          <div>
            <Label className="label-uppercase">Verifier notes</Label>
            <Textarea rows={3} value={form.verifier_notes} onChange={(e) => setForm({ ...form, verifier_notes: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800" />
          </div>
        </div>
        <DialogFooter className="gap-2 pt-3">
          <Button variant="outline" onClick={onClose} className="border-zinc-800 text-zinc-300 hover:bg-zinc-900">Cancel</Button>
          <Button onClick={submit} className="bg-white text-black hover:bg-zinc-200" data-testid="verify-submit">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UploadDialog({ open, onClose, assessmentId, subcategoryId, onUploaded }) {
  const [file, setFile] = useState(null);
  const [type, setType] = useState("policy");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setFile(null); setType("policy"); } }, [open]);

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("assessment_id", assessmentId);
      fd.append("subcategory_id", subcategoryId);
      fd.append("evidence_type", type);
      await api.post("/evidence/upload", fd);
      toast.success("Uploaded");
      onUploaded();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121212] border-zinc-800 text-white sm:max-w-md" data-testid="upload-evidence-dialog">
        <DialogHeader><DialogTitle>Upload Evidence</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-uppercase">File</Label>
            <Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="evidence-file-input" className="mt-1.5 bg-[#0A0A0A] border-zinc-800" />
          </div>
          <div>
            <Label className="label-uppercase">Evidence Type</Label>
            <select value={type} onChange={(e) => setType(e.target.value)} data-testid="evidence-type-select"
              className="mt-1.5 w-full h-10 px-3 rounded-md bg-[#0A0A0A] border border-zinc-800 text-sm text-white">
              {Object.entries(EVIDENCE_TYPE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
        </div>
        <DialogFooter className="gap-2 pt-3">
          <Button variant="outline" onClick={onClose} className="border-zinc-800 text-zinc-300 hover:bg-zinc-900">Cancel</Button>
          <Button onClick={submit} disabled={!file || busy} className="bg-white text-black hover:bg-zinc-200" data-testid="upload-submit">
            {busy ? "Uploading…" : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
